import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmt, MAT_TYPE } from "@/lib/ui";
import { toast } from "sonner";
import { Search, Truck, Pencil, Trash2, X } from "lucide-react";

const TYPES = [
  { key: "", label: "Tümü" },
  { key: "tel", label: "Tel" },
  { key: "tela", label: "Tela" },
  { key: "kraft", label: "Kraft" },
  { key: "lamet", label: "Lamet" },
  { key: "cring", label: "C-Ring" },
];

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return "—"; }
};

export default function Receipts() {
  const { isEditor } = useAuth();
  const [rows, setRows] = useState([]);
  const [type, setType] = useState("");
  const [search, setSearch] = useState("");
  const [supplier, setSupplier] = useState("");
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [ef, setEf] = useState({ quantity: "", irsaliye_no: "", anlasma_no: "" });

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/receipts?${type ? `type=${type}&` : ""}${search ? `search=${encodeURIComponent(search)}` : ""}`)
      .then((r) => setRows(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [type, search]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const openEdit = (r) => { setEdit(r); setEf({ quantity: String(r.quantity), irsaliye_no: r.irsaliye_no || "", anlasma_no: r.anlasma_no || "" }); };
  const saveEdit = async () => {
    try {
      await api.put(`/receipts/${edit.id}`, { quantity: parseFloat(ef.quantity), irsaliye_no: ef.irsaliye_no, anlasma_no: ef.anlasma_no });
      toast.success("Kayıt güncellendi, stok düzeltildi"); setEdit(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Bu gelen malzeme kaydını silmek istediğinize emin misiniz? Miktar stoktan geri alınır.")) return;
    try { await api.delete(`/receipts/${id}`); toast.success("Kayıt silindi, stok geri alındı"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6 animate-in-up" data-testid="receipts-page">
      <div>
        <h1 className="font-head text-3xl font-bold tracking-tight">Gelen Malzemeler Detay</h1>
        <p className="text-[#94A3B8] text-sm mt-1">
          Teslim alınan tüm malzemeler; irsaliye ve anlaşma numaralarıyla birlikte.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 text-[#64748B] absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="receipts-search-input" placeholder="Malzeme, tedarikçi, irsaliye veya anlaşma no..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-[#131A26] border border-[#212D40] rounded-lg pl-10 pr-4 py-2.5 text-sm outline-none focus:border-[#2563EB]" />
        </div>
        <select data-testid="receipts-supplier-filter" value={supplier} onChange={(e) => setSupplier(e.target.value)}
          className="bg-[#131A26] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]">
          <option value="">Tüm Tedarikçiler</option>
          {[...new Set(rows.map((r) => r.supplier).filter(Boolean))].sort().map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="flex gap-1.5 flex-wrap">
          {TYPES.map((t) => (
            <button key={t.key} data-testid={`receipts-type-${t.key || "all"}`} onClick={() => setType(t.key)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                type === t.key ? "bg-[#2563EB] text-white" : "bg-[#131A26] border border-[#212D40] text-[#94A3B8] hover:text-white"
              }`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-[#131A26] border border-[#212D40] rounded-xl overflow-hidden">
        <div className="overflow-x-auto max-h-[calc(100vh-300px)] overflow-y-auto">
          <table className="w-full text-sm" data-testid="receipts-table">
            <thead className="sticky top-0 bg-[#0F1520] z-10">
              <tr className="text-left text-[#94A3B8] text-xs uppercase tracking-wider border-b border-[#212D40]">
                <th className="py-3 px-5">Tarih</th>
                <th className="py-3 px-5">Malzeme</th>
                <th className="py-3 px-5">Tür</th>
                <th className="py-3 px-5">Tedarikçi</th>
                <th className="py-3 px-5 text-right">Miktar</th>
                <th className="py-3 px-5">Varış Hf</th>
                <th className="py-3 px-5">İrsaliye No</th>
                <th className="py-3 px-5">Anlaşma No</th>
                <th className="py-3 px-5"></th>
              </tr>
            </thead>
            <tbody>
              {!loading && rows.filter((r) => !supplier || r.supplier === supplier).map((r) => {
                const t = MAT_TYPE[r.material_type] || {};
                return (
                  <tr key={r.id} className="border-b border-[#1a2334] hover:bg-[#0F1520]" data-testid={`receipt-row-${r.id}`}>
                    <td className="py-3 px-5 font-num text-[#94A3B8]">{fmtDate(r.date)}</td>
                    <td className="py-3 px-5 font-medium flex items-center gap-2">
                      <Truck className="w-4 h-4" style={{ color: t.color }} /> {r.material_name}
                    </td>
                    <td className="py-3 px-5">
                      <span className="text-xs px-2 py-0.5 rounded" style={{ color: t.color, background: `${t.color}22` }}>{t.label}</span>
                    </td>
                    <td className="py-3 px-5 text-[#94A3B8]">{r.supplier || "—"}</td>
                    <td className="py-3 px-5 text-right font-num text-[#10B981]">+{fmt(r.quantity)} {r.unit}</td>
                    <td className="py-3 px-5 font-num text-[#94A3B8]">{r.expected_week || "—"}</td>
                    <td className="py-3 px-5 font-num">{r.irsaliye_no || <span className="text-[#64748B]">—</span>}</td>
                    <td className="py-3 px-5 font-num">{r.anlasma_no || <span className="text-[#64748B]">—</span>}</td>
                    <td className="py-3 px-5 text-right whitespace-nowrap">
                      {isEditor && (
                        <>
                          <button data-testid={`receipt-edit-${r.id}`} onClick={() => openEdit(r)} className="text-[#64748B] hover:text-[#2563EB] mr-3"><Pencil className="w-4 h-4" /></button>
                          <button data-testid={`receipt-delete-${r.id}`} onClick={() => del(r.id)} className="text-[#64748B] hover:text-[#EF4444]"><Trash2 className="w-4 h-4" /></button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
              {loading && <tr><td colSpan={9} className="py-10 text-center text-[#64748B]">Yükleniyor...</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={9} className="py-10 text-center text-[#64748B]">Henüz teslim alınan malzeme yok.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="bg-[#131A26] border border-[#212D40] rounded-xl w-full max-w-sm">
            <div className="flex items-center justify-between p-5 border-b border-[#212D40]">
              <h3 className="font-head text-xl font-semibold">Gelen Malzemeyi Düzenle</h3>
              <button onClick={() => setEdit(null)}><X className="w-5 h-5 text-[#94A3B8]" /></button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-[#94A3B8]">{edit.material_name}</p>
              <div>
                <label className="text-xs text-[#94A3B8] uppercase tracking-wider">Miktar ({edit.unit})</label>
                <input data-testid="receipt-edit-qty" type="number" value={ef.quantity} onChange={(e) => setEf({ ...ef, quantity: e.target.value })}
                  className="w-full mt-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#94A3B8] uppercase tracking-wider">İrsaliye No</label>
                  <input data-testid="receipt-edit-irsaliye" value={ef.irsaliye_no} onChange={(e) => setEf({ ...ef, irsaliye_no: e.target.value })}
                    className="w-full mt-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]" />
                </div>
                <div>
                  <label className="text-xs text-[#94A3B8] uppercase tracking-wider">Anlaşma No</label>
                  <input data-testid="receipt-edit-anlasma" value={ef.anlasma_no} onChange={(e) => setEf({ ...ef, anlasma_no: e.target.value })}
                    className="w-full mt-1 bg-[#0F1520] border border-[#212D40] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#2563EB]" />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-[#212D40]">
              <button onClick={() => setEdit(null)} className="px-4 py-2 rounded-lg text-sm text-[#94A3B8] hover:bg-[#0F1520]">İptal</button>
              <button data-testid="receipt-edit-save" onClick={saveEdit} className="px-5 py-2 rounded-lg text-sm font-semibold bg-[#2563EB] hover:bg-[#1D4ED8]">Kaydet</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
